import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';

const GOOGLE_SCRIPT_URL = process.env.REACT_APP_GOOGLE_SCRIPT_URL || '';

export default function RSVPModal({ onClose }) {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    attending: '',
    comment: '',
  });
  const [status, setStatus] = useState('idle'); // idle | loading | success | error | editing
  const [, setExistingRsvp] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.firstName.trim() || !formData.lastName.trim() || !formData.attending) {
      setErrorMessage('Please fill in all required fields.');
      return;
    }

    setStatus('loading');
    setErrorMessage('');

    try {
      await fetch(GOOGLE_SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'submit',
          ...formData,
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          comment: formData.comment.trim(),
        }),
      });

      // no-cors returns opaque response, so we assume success
      setStatus('success');
    } catch (err) {
      setErrorMessage('Something went wrong. Please try again.');
      setStatus('error');
    }
  };

  const handleLookup = async () => {
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      setErrorMessage('Please enter your first and last name to look up your RSVP.');
      return;
    }

    setStatus('loading');
    setErrorMessage('');

    try {
      const params = new URLSearchParams({
        action: 'lookup',
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
      });
      const response = await fetch(`${GOOGLE_SCRIPT_URL}?${params}`);
      const data = await response.json();

      if (data.found) {
        setExistingRsvp(data);
        setFormData({
          firstName: data.firstName,
          lastName: data.lastName,
          attending: data.attending,
          comment: data.comment || '',
        });
        setStatus('editing');
      } else {
        setErrorMessage('No existing RSVP found with that name.');
        setStatus('idle');
      }
    } catch (err) {
      setErrorMessage('Could not look up RSVP. Please try again.');
      setStatus('idle');
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  if (status === 'success') {
    return (
      <Overlay onClick={handleOverlayClick}>
        <ModalContainer>
          <CloseButton onClick={onClose}>&times;</CloseButton>
          <SuccessContent>
            <SuccessIcon>&#10003;</SuccessIcon>
            <SuccessTitle>Thank You!</SuccessTitle>
            <SuccessText>
              {formData.attending === 'yes'
                ? "We can't wait to celebrate with you!"
                : "We'll miss you! Thanks for letting us know."}
            </SuccessText>
            <CloseModalButton onClick={onClose}>Close</CloseModalButton>
          </SuccessContent>
        </ModalContainer>
      </Overlay>
    );
  }

  return (
    <Overlay onClick={handleOverlayClick}>
      <ModalContainer>
        <CloseButton onClick={onClose}>&times;</CloseButton>
        <ModalTitle>RSVP</ModalTitle>
        <ModalSubtitle>
          {status === 'editing'
            ? 'Update your RSVP below'
            : "We'd love to know if you can make it!"}
        </ModalSubtitle>

        <Form onSubmit={handleSubmit}>
          <FormRow>
            <FormGroup>
              <Label htmlFor="firstName">First Name *</Label>
              <Input
                id="firstName"
                name="firstName"
                type="text"
                value={formData.firstName}
                onChange={handleChange}
                placeholder="First name"
                required
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="lastName">Last Name *</Label>
              <Input
                id="lastName"
                name="lastName"
                type="text"
                value={formData.lastName}
                onChange={handleChange}
                placeholder="Last name"
                required
              />
            </FormGroup>
          </FormRow>

          <FormGroup>
            <Label>Will you be attending? *</Label>
            <RadioGroup>
              <RadioLabel>
                <RadioInput
                  type="radio"
                  name="attending"
                  value="yes"
                  checked={formData.attending === 'yes'}
                  onChange={handleChange}
                />
                <RadioCustom $checked={formData.attending === 'yes'} />
                Joyfully Accepts
              </RadioLabel>
              <RadioLabel>
                <RadioInput
                  type="radio"
                  name="attending"
                  value="no"
                  checked={formData.attending === 'no'}
                  onChange={handleChange}
                />
                <RadioCustom $checked={formData.attending === 'no'} />
                Regretfully Declines
              </RadioLabel>
            </RadioGroup>
          </FormGroup>

          <FormGroup>
            <Label htmlFor="comment">Leave a Comment</Label>
            <TextArea
              id="comment"
              name="comment"
              value={formData.comment}
              onChange={handleChange}
              placeholder="A message for the bride-to-be..."
              rows={3}
            />
          </FormGroup>

          {errorMessage && <ErrorText>{errorMessage}</ErrorText>}

          <SubmitButton type="submit" disabled={status === 'loading'}>
            {status === 'loading' ? 'Submitting...' : status === 'editing' ? 'Update RSVP' : 'Submit RSVP'}
          </SubmitButton>
        </Form>

        <LookupLink onClick={handleLookup}>
          Already RSVP'd? Click here to update your response
        </LookupLink>
      </ModalContainer>
    </Overlay>
  );
}

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { transform: translateY(30px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  animation: ${fadeIn} 0.2s ease;
`;

const ModalContainer = styled.div`
  background: #FFF5F5;
  border-radius: 12px;
  padding: 40px;
  max-width: 480px;
  width: 100%;
  position: relative;
  max-height: 90vh;
  overflow-y: auto;
  animation: ${slideUp} 0.3s ease;

  @media (max-width: 480px) {
    padding: 28px 22px;
    max-height: 95vh;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 16px;
  background: none;
  border: none;
  font-size: 1.8rem;
  color: #8B3A3A;
  line-height: 1;

  &:hover {
    color: #5C1A1B;
  }
`;

const ModalTitle = styled.h2`
  font-family: 'Josefin Sans', sans-serif;
  font-weight: 600;
  font-size: 1.8rem;
  letter-spacing: 4px;
  color: #5C1A1B;
  text-align: center;
  margin-bottom: 6px;
`;

const ModalSubtitle = styled.p`
  font-family: 'Cormorant Garamond', serif;
  font-style: italic;
  font-size: 1.1rem;
  color: #8B3A3A;
  text-align: center;
  margin-bottom: 28px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormRow = styled.div`
  display: flex;
  gap: 16px;

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 20px;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const Label = styled.label`
  font-family: 'Josefin Sans', sans-serif;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 1px;
  color: #5C1A1B;
  margin-bottom: 6px;
`;

const Input = styled.input`
  padding: 10px 14px;
  border: 1px solid #D4A0A0;
  border-radius: 6px;
  font-size: 1rem;
  color: #5C1A1B;
  background: #FFFFFF;
  transition: border-color 0.2s;

  &::placeholder {
    color: #C4A0A0;
  }

  &:focus {
    border-color: #8B3A3A;
    outline: none;
  }
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 20px;
  margin-top: 4px;

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.05rem;
  color: #5C1A1B;
  cursor: pointer;
`;

const RadioInput = styled.input`
  display: none;
`;

const RadioCustom = styled.span`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid ${props => props.$checked ? '#8B3A3A' : '#D4A0A0'};
  background: ${props => props.$checked ? '#8B3A3A' : 'transparent'};
  transition: all 0.2s;
  flex-shrink: 0;

  ${props => props.$checked && `
    box-shadow: inset 0 0 0 3px #FFF5F5;
  `}
`;

const TextArea = styled.textarea`
  padding: 10px 14px;
  border: 1px solid #D4A0A0;
  border-radius: 6px;
  font-size: 1rem;
  color: #5C1A1B;
  background: #FFFFFF;
  resize: vertical;
  min-height: 80px;
  transition: border-color 0.2s;

  &::placeholder {
    color: #C4A0A0;
  }

  &:focus {
    border-color: #8B3A3A;
    outline: none;
  }
`;

const ErrorText = styled.p`
  color: #C0392B;
  font-size: 0.9rem;
  text-align: center;
`;

const SubmitButton = styled.button`
  padding: 14px;
  background: #5C1A1B;
  color: #FFFFFF;
  border: none;
  border-radius: 6px;
  font-family: 'Josefin Sans', sans-serif;
  font-weight: 600;
  font-size: 1rem;
  letter-spacing: 2px;
  transition: background 0.3s;
  margin-top: 4px;

  &:hover:not(:disabled) {
    background: #8B3A3A;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const LookupLink = styled.button`
  display: block;
  width: 100%;
  background: none;
  border: none;
  margin-top: 18px;
  font-family: 'Cormorant Garamond', serif;
  font-style: italic;
  font-size: 0.95rem;
  color: #8B3A3A;
  text-decoration: underline;
  text-align: center;

  &:hover {
    color: #5C1A1B;
  }
`;

const SuccessContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 0;
`;

const SuccessIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: #8B3A3A;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
  margin-bottom: 16px;
`;

const SuccessTitle = styled.h2`
  font-family: 'Josefin Sans', sans-serif;
  font-weight: 600;
  font-size: 1.6rem;
  letter-spacing: 2px;
  color: #5C1A1B;
  margin-bottom: 10px;
`;

const SuccessText = styled.p`
  font-family: 'Cormorant Garamond', serif;
  font-style: italic;
  font-size: 1.15rem;
  color: #8B3A3A;
  text-align: center;
  margin-bottom: 24px;
`;

const CloseModalButton = styled.button`
  padding: 10px 40px;
  background: #5C1A1B;
  color: #FFFFFF;
  border: none;
  border-radius: 6px;
  font-family: 'Josefin Sans', sans-serif;
  font-weight: 600;
  font-size: 0.9rem;
  letter-spacing: 2px;
  transition: background 0.3s;

  &:hover {
    background: #8B3A3A;
  }
`;
